<template>
  <card-container
    id="ebt-card"
    @submit="submitForm"
    @reset="resetForm"
  >
    <template #title>
      EBT / Snap Card details
    </template>

    <template #content>
      <fieldset>
        <div class="row align-items-center">
          <label class="col-sm-3 control-label"> EBT Card Number* </label>
          <div class="col-sm-8 d-flex">
            <sanitized-input
              id="cardNumber"
              v-model="ebtCardInfo.cardNumber"
              required
            />
          </div>
        </div>
        <div class="row align-items-center">
          <label class="col-sm-3 control-label"> EBT PIN Number* </label>
          <div class="col-sm-8 d-flex">
            <sanitized-input
              id="pinNumber"
              v-model="ebtCardInfo.pinNumber"
              required
            />
          </div>
        </div>
      </fieldset>
    </template>

    <template #submit-button>
      Save
    </template>

    <template #additional-buttons>
      <p-button
        type="yellow"
        round
        class="submit-button"
        :disabled="!customerHasEbtCard"
        @click="removeEbtCard"
      >
        Remove EBT Card
      </p-button>
    </template>
  </card-container>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { ElLoading } from 'element-plus';

import util from '@/Utils/util';
import { useCustomer } from '@/use/customer';
import { useSwalDialog } from '@/use/swalDialog';
import { callerService } from '@/services/caller';

const { newCustomerInfo, setNewCustomerInfo } = useCustomer();
const { showSuccessPopupDialog, showErrorPopupDialog } = useSwalDialog();

const ebtCardInfo = ref({
  cardNumber: '',
  pinNumber: '',
});

const customerHasEbtCard = computed(() => {
  const ebtCard = newCustomerInfo.value.ebtCard;
  return Boolean(ebtCard?.cardNumber) || Boolean(ebtCard?.pinNumber);
});

const submitForm = async () => {
  await saveEbtCard({
    loadingMessage: 'Saving EBT / Snap Card info...',
    successMessage: 'EBT / Snap Card saved successfully!',
  });
};

const removeEbtCard = async () => {
  ebtCardInfo.value.cardNumber = '';
  ebtCardInfo.value.pinNumber = '';

  await saveEbtCard({
    loadingMessage: 'Removing EBT / Snap Card info...',
    successMessage: 'EBT / Snap Card removed successfully!',
  });
};

const resetForm = () => {
  ebtCardInfo.value.cardNumber = newCustomerInfo.value.ebtCard?.cardNumber || '';
  ebtCardInfo.value.pinNumber = newCustomerInfo.value.ebtCard?.pinNumber || '';
};

const saveEbtCard = async ({ loadingMessage, successMessage }: { loadingMessage: string; successMessage: string }) => {
  const loading = ElLoading.service({
    lock: true,
    text: loadingMessage,
    background: 'rgba(0, 0, 0, 0.7)',
    customClass: 'custom-full-spinner-class',
  });

  const postData = {
    ebtCard: {
      cardNumber: ebtCardInfo.value.cardNumber,
      pinNumber: ebtCardInfo.value.pinNumber,
    },
  };

  try {
    await callerService.updateCaller({
      callerId: newCustomerInfo.value.id,
      data: postData,
    });

    setNewCustomerInfo({
      ...newCustomerInfo.value,
      ebtCard: { ...ebtCardInfo.value },
    });

    showSuccessPopupDialog({
      text: successMessage,
    });
  } catch (error) {
    const errorText = util.getErrorResponse(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  }

  loading.close();
};

onMounted(() => {
  ebtCardInfo.value.cardNumber = newCustomerInfo.value.ebtCard?.cardNumber || '';
  ebtCardInfo.value.pinNumber = newCustomerInfo.value.ebtCard?.pinNumber || '';
});
</script>

<style lang="css" scoped>
#form-snap-card {
  border-bottom: 1px solid #f0f0f0;
  padding: 5px 0;
}
</style>
